import { useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, X, Send } from "lucide-react";

/**
 * VirtualMeetingButton — placed on every non-admin partner dashboard.
 * Access to actual meeting creation is centralised with the admin: this
 * component only *requests* a meeting.  The admin authorises it from the
 * admin panel and the join link is emailed back to the partner.
 *
 * Privacy: this button never renders any list of other users — the request
 * is strictly with "The Vinci Trade Desk".
 */
export function VirtualMeetingButton({ api }) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [agenda, setAgenda] = useState("");
  const [duration, setDuration] = useState(30);
  const [preferred, setPreferred] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!topic.trim() || topic.trim().length < 3) { toast.error("Please provide a topic (min 3 chars)"); return; }
    setBusy(true);
    try {
      const body = { topic: topic.trim(), agenda: agenda.trim(), duration_mins: Number(duration) };
      if (preferred) body.preferred_start = new Date(preferred).toISOString();
      await api.post("/meeting-requests", body);
      toast.success("Request sent to the Vinci Trade Desk. You'll receive the join link by email.");
      setOpen(false); setTopic(""); setAgenda(""); setPreferred("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit the request");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="virtual-meeting-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] hover:opacity-95"
      >
        <Video size={13} /> Start Virtual Meeting
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4" onClick={() => !busy && setOpen(false)} data-testid="virtual-meeting-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-[#D8B23E]/40 rounded-sm p-6 md:p-8 relative">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-[#8A8A82] hover:text-[#F5F5F0]"><X size={18} /></button>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-sm bg-black border border-[#D8B23E]/40 grid place-items-center">
                <Video size={16} className="text-[#D8B23E]" />
              </div>
              <div>
                <div className="font-serif text-2xl">Request a Virtual Meeting</div>
                <div className="font-body text-[11px] text-[#8A8A82]">
                  Powered by <b className="text-[#F5F5F0]">Jitsi Meet</b> · Zoho Meeting & Google Meet available on request.
                  The Vinci Trade Desk authorises and shares the join link.
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block">
                <div className="font-grotesk text-[10px] tracking-[0.22em] text-[#8A8A82] uppercase mb-1">Topic *</div>
                <input
                  data-testid="vm-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Quarterly Trade Review"
                  className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"
                />
              </label>
              <label className="block">
                <div className="font-grotesk text-[10px] tracking-[0.22em] text-[#8A8A82] uppercase mb-1">Agenda</div>
                <textarea
                  data-testid="vm-agenda"
                  value={agenda}
                  onChange={(e) => setAgenda(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E] resize-none"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="font-grotesk text-[10px] tracking-[0.22em] text-[#8A8A82] uppercase mb-1">Preferred start</div>
                  <input
                    data-testid="vm-preferred"
                    type="datetime-local"
                    value={preferred}
                    onChange={(e) => setPreferred(e.target.value)}
                    className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"
                  />
                </label>
                <label className="block">
                  <div className="font-grotesk text-[10px] tracking-[0.22em] text-[#8A8A82] uppercase mb-1">Duration (min)</div>
                  <input
                    data-testid="vm-duration"
                    type="number"
                    min={15}
                    max={240}
                    step={15}
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"
                  />
                </label>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <span className="font-body text-[10px] text-[#5f5f58]">Access controlled by Admin.</span>
              <button
                data-testid="vm-submit"
                onClick={submit}
                disabled={busy || !topic.trim()}
                className="inline-flex items-center gap-2 rounded-sm bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
